/* ── Previewing a quote's Xero PDF inside SEED EMERGE ─────────────────────
 *
 * Owner decisions 2026-10-25. ONE copy of the gates and the wording behind the
 * in-app PDF viewer (`components/quotes/QuotePdfPreview.tsx`) and every place
 * that offers it: the quote page, the Quotes list, the deal page, the Deals
 * list, the signature modal and the dashboards. No React here.
 *
 * - Preview needs only `quotes.read` (server: `GET /quotes/:id/pdf/preview`).
 *   Download inside the viewer still needs `quotes.export`; that gate is the
 *   viewer's, via `ability.canExport('quotes')`.
 * - "Send to Xero and preview" needs `quotes.update` (server:
 *   `POST /quotes/:id/xero/push`).
 * - The refusal sentences reuse `pdfRefusalMessage` in `@/lib/document-pdf`
 *   and differ only where the preview words a refusal differently from a
 *   download (a quote that is not in Xero has no PDF to PREVIEW).
 * ------------------------------------------------------------------------ */

import { ApiError } from './api';
import { pdfRefusalMessage, XERO_DOCUMENT_NOT_IN_XERO_CODE } from './document-pdf';
import { formatDateTime } from './format';
import type { Ability } from './permissions';
import type { XeroPushReport } from './xero';

export const PREVIEW_PDF_LABEL = 'Preview PDF';
export const CREATE_AND_PREVIEW_LABEL = 'Create and preview';
export const SEND_TO_XERO_AND_PREVIEW_LABEL = 'Send to Xero and preview';

/** Preview is a read: `quotes.read`, any scope (`own` counts; the server narrows). */
export function canPreviewQuotePdf(ability: Ability): boolean {
  return ability.canRead('quotes');
}

/** "Send to Xero and preview" writes the quote to Xero: `quotes.update`. */
export function canPushQuoteToXero(ability: Ability): boolean {
  return ability.canUpdate('quotes');
}

/**
 * The one line shown when SEED EMERGE holds edits Xero's copy does not
 * (`x-pdf-behind: 1`). A fact about the data, not helper text.
 */
export function quotePdfBehindNotice(syncedAt: string | null): string {
  if (syncedAt == null) return 'Changes made in SEED EMERGE are not in this PDF.';
  return `Changes made in SEED EMERGE since ${formatDateTime(syncedAt)} are not in this PDF.`;
}

/**
 * Why the preview could not be shown. Matched on `error.code`, never the
 * status; anything unrecognised falls through to `pdfRefusalMessage`, which in
 * turn falls through to the server's own message.
 */
export function quotePdfPreviewRefusal(error: unknown): string {
  if (error instanceof ApiError && error.code === XERO_DOCUMENT_NOT_IN_XERO_CODE) {
    return 'This quote is not in Xero, so there is no PDF to preview.';
  }
  return pdfRefusalMessage(error, 'quote');
}

/**
 * The push's own sentence when "Send to Xero and preview" did not push, or
 * `null` when it did (the viewer then loads the PDF).
 */
export function quotePushRefusal(outcome: XeroPushReport): string | null {
  switch (outcome.status) {
    case 'pushed':
      return null;
    case 'skipped':
      return outcome.reason;
    case 'failed':
      return outcome.message;
    default:
      /*
       * A status this build has never heard of. It is not `pushed`, so the PDF
       * cannot be assumed to exist; say so rather than guess.
       */
      return 'The quote could not be sent to Xero.';
  }
}

/** Arrival link that opens the preview on the quote page. */
export function quotePreviewHref(id: number): string {
  return `/quotes/${id}?preview=1`;
}

/** Arrival link that opens Send for signature on the quote page. */
export function quoteSignHref(id: number): string {
  return `/quotes/${id}?sign=1`;
}
